import { TranscriptEntry, User } from '@highlight/common'
import { resolveMicLabel } from '@shared/utils/audio-notes/mic-label'
import { GlobalState } from '@highlight/ipc'
import { USER_STATE } from '@shared/ipc/GlobalStateUtils'
import { getUserFullName } from '@renderer/utils/user-name'

const currentOwnerNameSnapshot = (): string | undefined =>
  getUserFullName(GlobalState.get<User | undefined>(USER_STATE))

/** Metadata flag: audioNote chip text should be filled from the live feed at send. */
export const LIVE_TRANSCRIPT_PENDING_KEY = 'liveTranscriptPending'

export function mapTranscriptEntriesToTranscriptString(
  transcriptEntries?: TranscriptEntry[],
  ownerNameOverride?: string,
): string {
  if (!transcriptEntries || transcriptEntries.length === 0) return ''
  const ownerName = ownerNameOverride ?? currentOwnerNameSnapshot()
  return transcriptEntries
    .map((entry) => {
      const speaker = entry.isHost ? resolveMicLabel(entry.speaker, ownerName) : entry.speaker
      return `${speaker} - ${new Date(entry.timestamp.absolute).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })}: ${entry.text}`
    })
    .join('\n')
}

/** The opening line of each kind of live send, which is also how a sent chip is recognised later. */
const LIVE_TRANSCRIPT_PREAMBLES = {
  full: '[This is a transcript from a meeting that is currently in progress. New entries will appear at the end as the meeting continues.]',
  update:
    '[The meeting is still in progress. This continues the transcript in your earlier messages: the first line repeats the last one you have, which may have been updated.]',
  unchanged:
    '[The meeting is still in progress. Nothing new has been said since the transcript in your earlier messages.]',
} as const

export type LiveTranscriptSendKind = keyof typeof LIVE_TRANSCRIPT_PREAMBLES

/**
 * Reads back a sent live meeting chip's text: which kind of send it was and
 * the transcript it carried. Null for a saved note's transcript.
 */
export function parseLiveTranscriptText(text: string): { kind: LiveTranscriptSendKind; transcript: string } | null {
  for (const kind of Object.keys(LIVE_TRANSCRIPT_PREAMBLES) as Array<LiveTranscriptSendKind>) {
    const preamble = LIVE_TRANSCRIPT_PREAMBLES[kind]
    if (text.startsWith(preamble)) {
      return { kind, transcript: text.slice(preamble.length).trim() }
    }
  }
  return null
}

/**
 * The live meeting chip's text for one send. `sentThrough` is the timestamp of
 * the last entry this chat already has. Earlier sends stay in the thread's
 * history, so a follow-up carries only what was said since. The last entry
 * already sent is repeated because speech recognition can still be revising it.
 */
export function buildLiveTranscriptText(
  entries: Array<TranscriptEntry>,
  sentThrough: string | undefined,
  ownerNameOverride?: string,
): string {
  if (sentThrough === undefined) {
    return `${LIVE_TRANSCRIPT_PREAMBLES.full}\n\n${mapTranscriptEntriesToTranscriptString(entries, ownerNameOverride)}`
  }
  const sentThroughMs = Date.parse(sentThrough)
  const isNew = (entry: TranscriptEntry) => Date.parse(entry.timestamp.absolute) > sentThroughMs
  if (!entries.some(isNew)) {
    return LIVE_TRANSCRIPT_PREAMBLES.unchanged
  }
  const update = entries.filter((entry) => Date.parse(entry.timestamp.absolute) >= sentThroughMs)
  return `${LIVE_TRANSCRIPT_PREAMBLES.update}\n\n${mapTranscriptEntriesToTranscriptString(update, ownerNameOverride)}`
}
